import React, { useState } from 'react';
import {
  Box,
  Typography,
  Card,
  CardContent,
  TextField,
  Button,
  Grid,
  Paper,
  Alert,
  Divider,
  CircularProgress,
  Avatar,
  Stack,
  InputAdornment,
} from '@mui/material';
import {
  Business,
  Save,
  Cancel,
  CheckCircle,
  Error as ErrorIcon,
  InfoOutlined,
  AccountBalance,
  Phone,
  Email,
  Language,
  LocationOn,
  Badge,
  People,
  ContentCopy,
  Check,
} from '@mui/icons-material';
import { useNavigate } from 'react-router-dom';
import { toast } from 'react-toastify';
import CompanyService from '../../services/companyService';
import AuthService from '../../services/authService';

const AddCompany = () => {
  const navigate = useNavigate();
  const currentUser = AuthService.getCurrentUser();

  const [formData, setFormData] = useState({
    name: '',
    vergiKimlikNo: '',
    vergiDairesi: '',
    naceCode: '',
    employeeCount: '',
    phone: '',
    address: '',
    email: '',
    website: '',
  });

  const [loading, setLoading] = useState(false);
  const [success, setSuccess] = useState(false);
  const [error, setError] = useState(null);
  const [createdCompanyId, setCreatedCompanyId] = useState(null);
  const [copied, setCopied] = useState(false);

  const handleChange = (field) => (event) => {
    setFormData({
      ...formData,
      [field]: event.target.value,
    });
    if (error) setError(null);
    if (success) setSuccess(false);
  };

  const validateForm = () => {
    if (!formData.name.trim()) {
      setError('Company name is required');
      return false;
    }
    if (!formData.vergiKimlikNo.trim()) {
      setError('Tax ID is required');
      return false;
    }
    if (!formData.naceCode.trim()) {
      setError('NACE code is required');
      return false;
    }
    if (!/^[1-9]\d{5}$/.test(formData.naceCode)) {
      setError('NACE code must be a 6-digit positive number');
      return false;
    }
    if (formData.employeeCount && !/^[1-9]\d*$/.test(formData.employeeCount)) {
      setError('Employee count must be a positive number');
      return false;
    }
    return true;
  };

  const handleSubmit = async (event) => {
    event.preventDefault();
    
    if (!validateForm()) {
      return;
    }

    if (!currentUser?.id) {
      toast.error('User information not found. Please login again.');
      return;
    }

    setLoading(true);
    setError(null);
    setSuccess(false);

    try {
      // Use id (UUID) instead of userId (integer)
      const userId = currentUser.id;

      const companyData = {
        userId: userId,
        name: formData.name.trim(),
        vergiKimlikNo: formData.vergiKimlikNo.trim(),
        vergiDairesi: formData.vergiDairesi.trim() || null,
        naceCode: parseInt(formData.naceCode),
        employeeCount: formData.employeeCount ? parseInt(formData.employeeCount) : null,
        phone: formData.phone.trim() || null,
        address: formData.address.trim() || null,
        email: formData.email.trim() || null,
        website: formData.website.trim() || null,
        status: 1,
      };

      console.log('Sending company data:', companyData); // Debug log
      const result = await CompanyService.createCompany(companyData);

      if (result.success) {
        setSuccess(true);
        setCreatedCompanyId(result.data?.id || null);
        toast.success('Company created successfully!');
        
        // Reset form
        setFormData({
          name: '',
          vergiKimlikNo: '',
          vergiDairesi: '',
          naceCode: '',
          employeeCount: '',
          phone: '',
          address: '',
          email: '',
          website: '',
        });
      } else {
        const errorMessage = typeof result.error === 'string' 
          ? result.error 
          : result.error?.message || result.error?.error || JSON.stringify(result.error) || 'Failed to create company';
        setError(errorMessage);
        toast.error(errorMessage);
      }
    } catch (err) {
      console.error('Create company error:', err);
      const errorMessage = err?.message || err?.error || 'An error occurred while creating the company';
      setError(errorMessage);
      toast.error(errorMessage);
    } finally {
      setLoading(false);
    }
  };

  const handleCancel = () => {
    navigate('/edefter/dashboard');
  };

  const handleCopyCompanyId = () => {
    if (createdCompanyId) {
      navigator.clipboard.writeText(createdCompanyId);
      setCopied(true);
      toast.success('Company ID copied to clipboard!');
      setTimeout(() => setCopied(false), 2000);
    }
  };

  return (
    <Box sx={{ 
      minHeight: '100vh',
      py: 4,
      px: { xs: 2, sm: 3 }
    }}>
      <Box sx={{ maxWidth: 900, mx: 'auto' }}>
        {/* Main Card */}
        <Card elevation={24} sx={{ 
          borderRadius: 4,
          overflow: 'hidden'
        }}>
          
          {/* User Info Banner */}
          <Box sx={{ 
            background: 'linear-gradient(135deg, #1e3c72 0%, #2a5298 100%)',
            p: 3,
            color: 'white'
          }}>
            <Grid container spacing={2} alignItems="center">
              <Grid item>
                <Avatar sx={{ 
                  width: 64, 
                  height: 64,
                  background: 'rgba(255,255,255,0.25)',
                  border: '3px solid rgba(255,255,255,0.3)',
                  fontSize: '1.5rem'
                }}>
                  <Business sx={{ fontSize: 32 }} />
                </Avatar>
              </Grid>
              <Grid item xs>
                <Typography variant="h6" sx={{ fontWeight: 600, mb: 0.5 }}>
                  Add New Company
                </Typography>
                <Typography variant="body2" sx={{ opacity: 0.9 }}>
                  Create a company profile for trial balance uploads
                </Typography>
              </Grid>
            </Grid>
          </Box>

          {/* Form */}
          <CardContent sx={{ p: 4 }}>
            <Box sx={{ mb: 4 }}>
              <Typography variant="h5" sx={{ fontWeight: 700, color: 'text.primary', mb: 1 }}>
                Company Information
              </Typography>
              <Typography variant="body2" color="text.secondary">
                Fill in the company details below. Required fields are marked with *.
              </Typography>
            </Box>
            <Box component="form" onSubmit={handleSubmit}>
              
              {/* Basic Information */}
              <Box sx={{ mb: 4 }}>
                <Typography variant="h6" sx={{ 
                  mb: 3, 
                  display: 'flex', 
                  alignItems: 'center',
                  fontWeight: 600,
                  color: 'text.primary'
                }}>
                  <Business sx={{ mr: 1.5, color: '#1976d2' }} />
                  Basic Information
                </Typography>
                
                <Grid container spacing={3}>
                  <Grid item xs={12} sm={6}>
                    <TextField
                      fullWidth
                      label="Company Name"
                      value={formData.name}
                      onChange={handleChange('name')}
                      required
                      variant="outlined"
                      InputProps={{
                        startAdornment: (
                          <InputAdornment position="start">
                            <Business color={formData.name ? 'primary' : 'disabled'} />
                          </InputAdornment>
                        ),
                      }}
                      sx={{
                        '& .MuiOutlinedInput-root': {
                          '&:hover fieldset': {
                            borderColor: '#1976d2',
                          },
                          '&.Mui-focused fieldset': {
                            borderColor: '#1976d2',
                          }
                        }
                      }}
                    />
                  </Grid>

                  <Grid item xs={12} sm={6}>
                    <TextField
                      fullWidth
                      label="Tax ID (Vergi Kimlik No)"
                      value={formData.vergiKimlikNo}
                      onChange={handleChange('vergiKimlikNo')}
                      required
                      variant="outlined"
                      InputProps={{
                        startAdornment: (
                          <InputAdornment position="start">
                            <Badge color={formData.vergiKimlikNo ? 'primary' : 'disabled'} />
                          </InputAdornment>
                        ),
                      }}
                      sx={{
                        '& .MuiOutlinedInput-root': {
                          '&:hover fieldset': {
                            borderColor: '#1976d2',
                          },
                          '&.Mui-focused fieldset': {
                            borderColor: '#1976d2',
                          }
                        }
                      }}
                    />
                  </Grid>

                  <Grid item xs={12} sm={6}>
                    <TextField
                      fullWidth
                      label="Tax Office (Vergi Dairesi)"
                      value={formData.vergiDairesi}
                      onChange={handleChange('vergiDairesi')}
                      variant="outlined"
                      InputProps={{
                        startAdornment: (
                          <InputAdornment position="start">
                            <AccountBalance color={formData.vergiDairesi ? 'primary' : 'disabled'} />
                          </InputAdornment>
                        ),
                      }}
                      sx={{
                        '& .MuiOutlinedInput-root': {
                          '&:hover fieldset': {
                            borderColor: '#1976d2',
                          },
                          '&.Mui-focused fieldset': {
                            borderColor: '#1976d2',
                          }
                        }
                      }}
                    />
                  </Grid>

                  <Grid item xs={12} sm={6}>
                    <TextField
                      fullWidth
                      label="NACE Code"
                      value={formData.naceCode}
                      onChange={handleChange('naceCode')}
                      required
                      variant="outlined"
                      inputProps={{
                        maxLength: 6,
                        pattern: '[1-9][0-9]{5}',
                      }}
                      helperText="6-digit positive number"
                      InputProps={{
                        startAdornment: (
                          <InputAdornment position="start">
                            <Badge color={formData.naceCode ? 'primary' : 'disabled'} />
                          </InputAdornment>
                        ),
                      }}
                      sx={{
                        '& .MuiOutlinedInput-root': {
                          '&:hover fieldset': {
                            borderColor: '#1976d2',
                          },
                          '&.Mui-focused fieldset': {
                            borderColor: '#1976d2',
                          }
                        }
                      }}
                    />
                  </Grid>

                  <Grid item xs={12} sm={6}>
                    <TextField
                      fullWidth
                      label="Employee Count"
                      type="number"
                      value={formData.employeeCount}
                      onChange={handleChange('employeeCount')}
                      variant="outlined"
                      inputProps={{ min: 1 }}
                      InputProps={{
                        startAdornment: (
                          <InputAdornment position="start">
                            <People color={formData.employeeCount ? 'primary' : 'disabled'} />
                          </InputAdornment>
                        ),
                      }}
                      sx={{
                        '& .MuiOutlinedInput-root': {
                          '&:hover fieldset': {
                            borderColor: '#1976d2',
                          },
                          '&.Mui-focused fieldset': {
                            borderColor: '#1976d2',
                          }
                        }
                      }}
                    />
                  </Grid>
                </Grid>
              </Box>

              <Divider sx={{ my: 4 }} />

              {/* Contact Information */}
              <Box sx={{ mb: 4 }}>
                <Typography variant="h6" sx={{ 
                  mb: 3, 
                  display: 'flex', 
                  alignItems: 'center',
                  fontWeight: 600,
                  color: 'text.primary'
                }}>
                  <Phone sx={{ mr: 1.5, color: '#1976d2' }} />
                  Contact Information
                </Typography>

                <Grid container spacing={3}>
                  <Grid item xs={12} sm={6}>
                    <TextField
                      fullWidth
                      label="Phone"
                      value={formData.phone}
                      onChange={handleChange('phone')}
                      variant="outlined"
                      InputProps={{
                        startAdornment: (
                          <InputAdornment position="start">
                            <Phone color={formData.phone ? 'primary' : 'disabled'} />
                          </InputAdornment>
                        ),
                      }}
                      sx={{
                        '& .MuiOutlinedInput-root': {
                          '&:hover fieldset': {
                            borderColor: '#1976d2',
                          },
                          '&.Mui-focused fieldset': {
                            borderColor: '#1976d2',
                          }
                        }
                      }}
                    />
                  </Grid>

                  <Grid item xs={12} sm={6}>
                    <TextField
                      fullWidth
                      label="Email"
                      type="email"
                      value={formData.email}
                      onChange={handleChange('email')}
                      variant="outlined"
                      InputProps={{
                        startAdornment: (
                          <InputAdornment position="start">
                            <Email color={formData.email ? 'primary' : 'disabled'} />
                          </InputAdornment>
                        ),
                      }}
                      sx={{
                        '& .MuiOutlinedInput-root': {
                          '&:hover fieldset': {
                            borderColor: '#1976d2',
                          },
                          '&.Mui-focused fieldset': {
                            borderColor: '#1976d2',
                          }
                        }
                      }}
                    />
                  </Grid>

                  <Grid item xs={12} sm={6}>
                    <TextField
                      fullWidth
                      label="Website"
                      value={formData.website}
                      onChange={handleChange('website')}
                      variant="outlined"
                      InputProps={{
                        startAdornment: (
                          <InputAdornment position="start">
                            <Language color={formData.website ? 'primary' : 'disabled'} />
                          </InputAdornment>
                        ),
                      }}
                      sx={{
                        '& .MuiOutlinedInput-root': {
                          '&:hover fieldset': {
                            borderColor: '#1976d2',
                          },
                          '&.Mui-focused fieldset': {
                            borderColor: '#1976d2',
                          }
                        }
                      }}
                    />
                  </Grid>

                  <Grid item xs={12}>
                    <TextField
                      fullWidth
                      label="Address"
                      value={formData.address}
                      onChange={handleChange('address')}
                      variant="outlined"
                      multiline
                      rows={3}
                      InputProps={{
                        startAdornment: (
                          <InputAdornment position="start" sx={{ alignSelf: 'flex-start', mt: 1.5 }}>
                            <LocationOn color={formData.address ? 'primary' : 'disabled'} />
                          </InputAdornment>
                        ),
                      }}
                      sx={{
                        '& .MuiOutlinedInput-root': {
                          '&:hover fieldset': {
                            borderColor: '#1976d2',
                          },
                          '&.Mui-focused fieldset': {
                            borderColor: '#1976d2',
                          }
                        }
                      }}
                    />
                  </Grid>
                </Grid>
              </Box>

              {/* Info Banner */}
              <Paper sx={{ 
                p: 2.5, 
                mb: 4, 
                backgroundColor: 'rgba(102, 126, 234, 0.08)',
                border: '1px solid rgba(102, 126, 234, 0.2)',
                borderRadius: 3
              }}>
                <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 2 }}>
                  <InfoOutlined sx={{ color: '#1976d2', mt: 0.5 }} />
                  <Box>
                    <Typography variant="subtitle2" sx={{ fontWeight: 600, mb: 0.5, color: '#1976d2' }}>
                      Important Information
                    </Typography>
                    <Typography variant="body2" color="text.secondary">
                      • Company name, Tax ID, and NACE code are required fields<br/>
                      • NACE code must be a 6-digit positive number<br/>
                      • After creating the company, you can upload trial balance files using the Company ID
                    </Typography>
                  </Box>
                </Box>
              </Paper>

              {/* Error/Success Messages */}
              {error && (
                <Alert severity="error" sx={{ mb: 3 }} onClose={() => setError(null)}>
                  {error}
                </Alert>
              )}

              {success && createdCompanyId && (
                <Alert 
                  severity="success" 
                  icon={<CheckCircle fontSize="inherit" />}
                  sx={{ mb: 3 }} 
                  onClose={() => {
                    setSuccess(false);
                    setCreatedCompanyId(null);
                  }}
                >
                  <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', flexWrap: 'wrap', gap: 1 }}>
                    <Box>
                      <Typography variant="body1" sx={{ fontWeight: 600, mb: 0.5 }}>
                        Company created successfully!
                      </Typography>
                      <Typography variant="body2">
                        Company ID: <strong>{createdCompanyId}</strong>
                      </Typography>
                      <Typography variant="body2" sx={{ mt: 1 }}>
                        You can now upload trial balance files for this company using this ID.
                      </Typography>
                    </Box>
                    <Button
                      variant="outlined"
                      size="small"
                      startIcon={copied ? <Check /> : <ContentCopy />}
                      onClick={handleCopyCompanyId}
                      sx={{
                        borderColor: 'success.main',
                        color: 'success.main',
                        '&:hover': {
                          borderColor: 'success.dark',
                          backgroundColor: 'rgba(76, 175, 80, 0.08)'
                        }
                      }}
                    >
                      {copied ? 'Copied!' : 'Copy ID'}
                    </Button>
                  </Box>
                </Alert>
              )}

              {/* Action Buttons */}
              <Stack direction="row" spacing={2} justifyContent="flex-end">
                <Button
                  variant="outlined"
                  onClick={handleCancel}
                  disabled={loading}
                  startIcon={<Cancel />}
                  size="large"
                  sx={{
                    borderColor: '#1976d2',
                    color: '#1976d2',
                    '&:hover': {
                      borderColor: '#764ba2',
                      backgroundColor: 'rgba(102, 126, 234, 0.04)'
                    }
                  }}
                >
                  Cancel
                </Button>

                <Button
                  type="submit"
                  variant="contained"
                  disabled={loading}
                  endIcon={loading ? <CircularProgress size={20} color="inherit" /> : <Save />}
                  size="large"
                  sx={{ 
                    minWidth: 180,
                    background: 'linear-gradient(135deg, #1976d2 0%, #1565c0 100%)',
                    boxShadow: '0 4px 15px rgba(25, 118, 210, 0.4)',
                    '&:hover': {
                      background: 'linear-gradient(135deg, #1565c0 0%, #0d47a1 100%)',
                      boxShadow: '0 6px 20px rgba(25, 118, 210, 0.6)',
                    }
                  }}
                >
                  {loading ? 'Creating...' : 'Create Company'}
                </Button>
              </Stack>
            </Box>
          </CardContent>
        </Card>
      </Box>
    </Box>
  );
};

export default AddCompany;
